import React, { useState } from 'react';
import Modal from './Modal';
import './LocationTypeModal.css';

const CONVEYOR_SORT_ORDER = 999;

const normalizeTypeForm = (form) => {
  const sortOrder = Number(form.sort_order) || 0;
  return {
    ...form,
    sort_order: sortOrder,
    has_rfid: sortOrder === CONVEYOR_SORT_ORDER
  };
};

const getDefaultSortOrder = (locationTypes) => {
  const normalTypeCount = locationTypes.filter(
    (type) => Number(type.sort_order) !== CONVEYOR_SORT_ORDER
  ).length;

  return normalTypeCount + 1;
};

const LocationTypeModal = ({ 
  company, 
  locationTypes, 
  onClose, 
  onAdd, 
  onEdit, 
  onDelete 
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState({
    type_code: '',
    type_name_en: '',
    type_name_ja: '',
    has_rfid: false,
    sort_order: 1,  // 並び順は1から始まる
    is_active: true
  });

  const isConveyorType = Number(typeForm.sort_order) === CONVEYOR_SORT_ORDER;
  const isReadOnlyConveyor = Boolean(editingType) && isConveyorType;

  const handleAdd = () => {
    setEditingType(null);
    setTypeForm(normalizeTypeForm({
      type_code: '',
      type_name_en: '',
      type_name_ja: '',
      has_rfid: false,
      sort_order: getDefaultSortOrder(locationTypes),
      is_active: true
    }));
    setShowForm(true);
  };

  const handleEditClick = (type) => {
    setEditingType(type);
    setTypeForm(normalizeTypeForm({
      type_code: type.type_code,
      type_name_en: type.type_name_en,
      type_name_ja: type.type_name_ja,
      has_rfid: type.has_rfid,
      sort_order: type.sort_order,
      is_active: type.is_active
    }));
    setShowForm(true);
  };

  const handleSave = () => {
    const normalizedTypeForm = normalizeTypeForm(typeForm);
    if (editingType) {
      onEdit(editingType.location_node_type_id, normalizedTypeForm);
    } else {
      onAdd(normalizedTypeForm);
    }
    setShowForm(false);
  };

  const handleDeleteClick = (type) => {
    if (Number(type.sort_order) === CONVEYOR_SORT_ORDER) {
      return;
    }

    if (window.confirm(`設置先種別「${type.type_name_ja}」を削除してもよろしいですか？\n\nこの種別を使用している設置先がある場合は削除できません。`)) {
      onDelete(type.location_node_type_id);
    }
  };

  return (
    <Modal
      title="設置先種別マスタ管理"
      onClose={onClose}
      width="900px"
    >
        {!showForm ? (
          <div className="location-types-list">
            <div className="list-header">
              <h3>{company.company_name}の設置先種別</h3>
              <button className="btn btn-primary" onClick={handleAdd}>
                + 新規追加
              </button>
            </div>

            <table className="types-table">
              <thead>
                <tr>
                  <th>並び順</th>
                  <th>種別コード</th>
                  <th>種別名(英語)</th>
                  <th>種別名(日本語)</th>
                  <th>RFID保持</th>
                  <th>状態</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {locationTypes.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                      設置先種別が登録されていません
                    </td>
                  </tr>
                ) : (
                  locationTypes.map((type) => (
                    <tr key={type.location_node_type_id}>
                      <td>{type.sort_order}</td>
                      <td><code>{type.type_code}</code></td>
                      <td>{type.type_name_en}</td>
                      <td><strong>{type.type_name_ja}</strong></td>
                      <td>
                        {type.has_rfid ? (
                          <span className="badge badge-success">有</span>
                        ) : (
                          <span className="badge badge-default">無</span>
                        )}
                      </td>
                      <td>
                        {type.is_active ? (
                          <span className="badge badge-success">有効</span>
                        ) : (
                          <span className="badge badge-inactive">無効</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-small btn-secondary"
                            onClick={() => handleEditClick(type)}
                          >
                            {Number(type.sort_order) === CONVEYOR_SORT_ORDER ? '表示' : '編集'}
                          </button>
                          <button
                            className="btn-small btn-danger"
                            onClick={() => handleDeleteClick(type)}
                            disabled={Number(type.sort_order) === CONVEYOR_SORT_ORDER}
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="location-type-form">
            <h3>{editingType ? (isReadOnlyConveyor ? '設置先種別詳細' : '設置先種別編集') : '設置先種別追加'}</h3>

            {isReadOnlyConveyor && (
              <div className="location-type-readonly-notice">
                コンベア種別はシステム固定項目のため、この画面では変更できません。
              </div>
            )}

            <div className="form-group">
              <label>種別コード *</label>
              <input
                type="text"
                value={typeForm.type_code}
                onChange={(e) => setTypeForm({ ...typeForm, type_code: e.target.value })}
                placeholder="例: conveyor"
                readOnly={isReadOnlyConveyor}
              />
              <small>英数字とアンダースコア推奨</small>
            </div>

            <div className="form-group">
              <label>種別名(英語) *</label>
              <input
                type="text"
                value={typeForm.type_name_en}
                onChange={(e) => setTypeForm({ ...typeForm, type_name_en: e.target.value })}
                placeholder="例: Conveyor"
                readOnly={isReadOnlyConveyor}
              />
            </div>

            <div className="form-group">
              <label>種別名(日本語) *</label>
              <input
                type="text"
                value={typeForm.type_name_ja}
                onChange={(e) => setTypeForm({ ...typeForm, type_name_ja: e.target.value })}
                placeholder="例: コンベア"
                readOnly={isReadOnlyConveyor}
              />
            </div>

            <div className="form-group">
              <label>並び順</label>
              <input
                type="number"
                value={typeForm.sort_order}
                onChange={(e) => setTypeForm(normalizeTypeForm({ ...typeForm, sort_order: parseInt(e.target.value, 10) || 0 }))}
                readOnly={isReadOnlyConveyor}
              />
              <small>表示順序を数値で指定します。コンベアは 999 固定運用で、RFID が自動的に有効になります。</small>
            </div>

            <div className="form-group">
              <label>RFIDタグを保持する階層</label>
              <div className="location-type-static-value">
                {typeForm.has_rfid ? (
                  <span className="badge badge-success">有効</span>
                ) : (
                  <span className="badge badge-default">無効</span>
                )}
              </div>
              <small>
                {isConveyorType
                  ? 'コンベア（sort_order=999）は RFID 有効が必須のため変更できません。'
                  : 'コンベア以外は RFID を有効化できません。'}
              </small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={typeForm.is_active}
                  onChange={(e) => setTypeForm({ ...typeForm, is_active: e.target.checked })}
                  disabled={isReadOnlyConveyor}
                  style={{ marginRight: '8px', width: 'auto' }}
                />
                有効
              </label>
            </div>

            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => setShowForm(false)}>
                {isReadOnlyConveyor ? '閉じる' : 'キャンセル'}
              </button>
              {!isReadOnlyConveyor && (
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={!typeForm.type_code || !typeForm.type_name_en || !typeForm.type_name_ja}
                >
                  {editingType ? '更新' : '追加'}
                </button>
              )}
            </div>
          </div>
        )}
    </Modal>
  );
};

export default LocationTypeModal;
